"use client";

import { forwardRef, useMemo, useState, type ReactNode } from "react";
import NextLink from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PublicRouteBridgeProvider, type PublicRouteBridge, type PublicRouteLinkProps } from "@unitfield/ui/public-rental/public-route-bridge";

/** Server data handed to the client as query cache entries, so the first render already has it (and crawlers see it). */
export interface QuerySeed {
  queryKey: readonly unknown[];
  data: unknown;
}

const StorefrontLink = forwardRef<HTMLAnchorElement, PublicRouteLinkProps>(function StorefrontLink({ to, state, ...props }, ref) {
  // Location state is a SPA convenience; every page here is server-rendered with its own data.
  void state;
  return <NextLink {...props} ref={ref} href={to} prefetch={false} />;
});

export function PublicProviders({ seeds, children }: { seeds: readonly QuerySeed[]; children: ReactNode }) {
  const [client] = useState(() => {
    const queryClient = new QueryClient({
      defaultOptions: { queries: { staleTime: 60_000, retry: false, refetchOnWindowFocus: false } },
    });
    for (const seed of seeds) queryClient.setQueryData(seed.queryKey, seed.data);
    return queryClient;
  });
  const router = useRouter();
  const pathname = usePathname();
  const search = useSearchParams();
  const searchText = search.toString();

  const bridge = useMemo<PublicRouteBridge>(
    () => ({
      Link: StorefrontLink,
      navigate: (to, options) => (options?.replace ? router.replace(to) : router.push(to)),
      searchParams: new URLSearchParams(searchText),
      setSearchParams: (updater, options) => {
        const next = updater(new URLSearchParams(searchText)).toString();
        const href = next ? `${pathname}?${next}` : pathname;
        if (options?.replace) router.replace(href, { scroll: false });
        else router.push(href, { scroll: false });
      },
    }),
    [pathname, router, searchText],
  );

  return (
    <QueryClientProvider client={client}>
      <PublicRouteBridgeProvider bridge={bridge}>{children}</PublicRouteBridgeProvider>
    </QueryClientProvider>
  );
}
